/**
 * Auth Service for LexiReview
 *
 * Handles all authentication with the lexidraft.in backend.
 * Tokens are stored in httpOnly cookies (set by the server).
 * The browser sends cookies automatically with credentials: 'include'.
 */

import axios from 'axios';
import { API_BASE_URL } from '@/config';

// Create axios instance with credentials so cookies are sent automatically
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/auth-server`,
  withCredentials: true,
});

// Types
export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  isEmailVerified: boolean;
  role: string;

  // Onboarding Fields
  phoneNumber?: string;
  companyName?: string;
  jobTitle?: string;
  industry?: string;
  primaryUse?: string;
  addressLine1?: string;
  addressLine2?: string;

  // User input fields from form
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;

  // DB returned fields
  addressCity?: string;
  addressState?: string;
  addressCountry?: string;
  addressPostalCode?: string;

  isOnboarded?: boolean;

  createdAt: string;
  updatedAt: string;
}

export interface LoginCredentials {
  identifier: string;
  password: string;
}

export interface SignupCredentials {
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: User;
    tokens: {
      accessToken: {
        token: string;
        expires: string;
      };
      refreshToken: {
        token: string;
        expires: string;
      };
    };
  };
}

class AuthService {
  private refreshTokenTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Track whether the user is authenticated (cookies are httpOnly so we can't read them). */
  private _isAuthenticated = false;

  get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  private onLoginSuccess(expiresIso?: string) {
    this._isAuthenticated = true;

    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
    this.setupTokenRefresh(expiresIso);
  }

  private setupTokenRefresh(expiresIso?: string) {
    if (!expiresIso) return;

    try {
      const expiresIn = new Date(expiresIso).getTime() - Date.now();
      const refreshTime = expiresIn - 5 * 60 * 1000; // 5 min before expiry

      if (refreshTime > 0) {
        this.refreshTokenTimeout = setTimeout(() => {
          this.refreshTokens().catch(() => {});
        }, refreshTime);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error setting up token refresh:', error);
      }
    }
  }

  /**
   * @deprecated Tokens are now stored in httpOnly cookies.
   * This returns an empty object for backward compatibility.
   */
  public getTokens() {
    return {
      accessToken: null,
      refreshToken: null,
    };
  }

  public clearTokens() {
    this._isAuthenticated = false;
    localStorage.removeItem('skipOnboarding');
    localStorage.removeItem('lexi_free_questions');
    // Clean up any legacy localStorage tokens from before the migration
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/signin', credentials);
      const { accessToken } = response.data.data.tokens;
      this.onLoginSuccess(accessToken.expires);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Login failed');
      }
      throw error;
    }
  }

  async signup(credentials: SignupCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/signup', credentials);
      const { accessToken } = response.data.data.tokens;
      this.onLoginSuccess(accessToken.expires);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Signup failed');
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Logout error:', error);
      }
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get('/auth/me');
      if (response.data?.data?.user) {
        this._isAuthenticated = true;
      }
      return response.data?.data?.user || null;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.clearTokens();
      }
      return null;
    }
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    try {
      const response = await api.put('/auth/me/profile', data);
      return response.data?.data?.user || response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to update profile');
      }
      throw error;
    }
  }

  async editProfile(data: Partial<User>): Promise<User> {
    try {
      const response = await api.put('/auth/me/edit-profile', data);
      return response.data?.data?.user || response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to update profile');
      }
      throw error;
    }
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    try {
      await api.put('/auth/me/change-password', data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to change password');
      }
      throw error;
    }
  }

  async refreshTokens(): Promise<void> {
    try {
      // The refresh token is sent automatically via cookie
      const response = await api.post('/auth/refresh-tokens');
      // Server sets new cookies automatically
      const accessToken = response.data?.data?.tokens?.accessToken;
      this.onLoginSuccess(accessToken?.expires);
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await api.post('/auth/forgot-password', { email });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to send reset code');
      }
      throw error;
    }
  }

  async resetPassword(email: string, code: string, password: string): Promise<void> {
    try {
      await api.post('/auth/reset-password', { email, code, password });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to reset password');
      }
      throw error;
    }
  }

  async verifyEmail(email: string, code: string): Promise<void> {
    try {
      await api.post('/auth/verify-email', { email, code });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to verify email');
      }
      throw error;
    }
  }

  async resendVerificationCode(email: string): Promise<void> {
    try {
      await api.post('/auth/resend-verification', { email });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'Failed to resend verification code');
      }
      throw error;
    }
  }
}

export const authService = new AuthService();

// Axios interceptors — no longer need to set Authorization header manually
// since cookies are sent automatically with withCredentials: true.

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await authService.refreshTokens();
        // Retry the original request — cookies are updated automatically
        return api(originalRequest);
      } catch {
        authService.clearTokens();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { api };
